<script lang="ts">
const DAY_MS = 24 * 60 * 60 * 1000
</script>
<template>
  <div :class="[bem.b(), bem.m(props.size)]" :style="cssVars">
    <div
      v-if="showIndicator && props.indicatorVerticalPosition === 'top'"
      :class="indicatorRowClass"
    >
      <slot name="indicator">
        <div v-if="props.showColorIndicator" :class="bem.e('indicator')">
          <span :class="bem.e('indicator-label')">
            <slot name="indicator-leading-text">少</slot>
          </span>
          <div :class="bem.e('indicator-cells')">
            <div
              v-for="(color, index) in indicatorColors"
              :key="`indicator-top-${index}`"
              :class="bem.e('indicator-cell')"
              :style="{ backgroundColor: color }"
            />
          </div>
          <span :class="bem.e('indicator-label')">
            <slot name="indicator-trailing-text">多</slot>
          </span>
        </div>
      </slot>
    </div>

    <div :class="bem.e('content')">
      <div
        v-if="props.showMonthLabels && heatmapMatrix.length"
        :class="bem.e('month-row')"
      >
        <div v-if="props.showWeekLabels" :class="bem.e('month-spacer')" />
        <div
          v-for="(label, index) in monthLabels"
          :key="`month-${index}`"
          :class="bem.e('month-label-cell')"
        >
          {{ label }}
        </div>
      </div>

      <div :class="bem.e('calendar')">
        <div v-if="props.showWeekLabels" :class="bem.e('week-labels')">
          <div
            v-for="(label, index) in weekLabels"
            :key="`week-${index}`"
            :class="bem.e('week-label-cell')"
          >
            {{ label }}
          </div>
        </div>

        <div :class="bem.e('weeks')">
          <div
            v-for="(week, weekIndex) in heatmapMatrix"
            :key="`heatmap-week-${weekIndex}`"
            :class="bem.e('week-column')"
          >
            <div
              v-for="(cell, dayIndex) in week"
              :key="getCellKey(cell, weekIndex, dayIndex)"
              :class="bem.e('cell-wrap')"
            >
              <template v-if="cell">
                <LlTooltip
                  v-if="tooltipEnabled && !cell.hidden"
                  v-bind="tooltipBindings"
                  :tip="getTooltipText(cell.data)"
                  :show-container="false"
                >
                  <template v-if="slots.tooltip" #title>
                    <slot
                      name="tooltip"
                      :timestamp="cell.data.timestamp"
                      :value="cell.data.value"
                    />
                  </template>
                  <div
                    :class="getCellClass(cell)"
                    :style="getCellStyle(cell)"
                  />
                </LlTooltip>
                <div
                  v-else
                  :class="getCellClass(cell)"
                  :style="getCellStyle(cell)"
                />
              </template>

              <div v-else :class="[bem.e('cell'), bem.is('empty')]" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="slots.footer" :class="bem.e('footer')">
      <div :class="bem.e('footer-left')">
        <slot name="footer" />
      </div>
    </div>

    <div
      v-if="showIndicator && props.indicatorVerticalPosition === 'bottom'"
      :class="indicatorRowClass"
    >
      <slot name="indicator">
        <div v-if="props.showColorIndicator" :class="bem.e('indicator')">
          <span :class="bem.e('indicator-label')">
            <slot name="indicator-leading-text">少</slot>
          </span>
          <div :class="bem.e('indicator-cells')">
            <div
              v-for="(color, index) in indicatorColors"
              :key="`indicator-bottom-${index}`"
              :class="bem.e('indicator-cell')"
              :style="{ backgroundColor: color }"
            />
          </div>
          <span :class="bem.e('indicator-label')">
            <slot name="indicator-trailing-text">多</slot>
          </span>
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { addUnit, createNamespace } from '@ll-plus/utils'
import { computed, useSlots } from 'vue'

import { LlTooltip } from '../../tooltip'
import {
  heatmapColorThemes,
  heatmapProps,
  type HeatmapDataItem,
  type HeatmapTooltipOptions,
} from './heatmap'

defineOptions({ name: 'LlHeatmap' })

interface HeatmapCellData extends HeatmapDataItem {
  hidden: boolean
  synthetic: boolean
}

interface HeatmapCell {
  data: HeatmapCellData
  color: string
  hidden: boolean
  loading: boolean
}



const SIZE_MAP = {
  small: {
    rectSize: 10,
    radius: 2,
    fontSize: 12,
    labelWidth: 28,
  },
  medium: {
    rectSize: 12,
    radius: 3,
    fontSize: 12,
    labelWidth: 32,
  },
  large: {
    rectSize: 15,
    radius: 4,
    fontSize: 13,
    labelWidth: 36,
  },
} as const

const props = defineProps(heatmapProps)
const slots = useSlots()
const bem = createNamespace('heatmap')

const showIndicator = computed(() => {
  return props.showColorIndicator || !!slots.indicator
})

const tooltipOptions = computed<Required<HeatmapTooltipOptions>>(() => {
  return {
    show: false,
    placement: 'top',
    arrow: true,
    bgColor: undefined,
    textColor: undefined,
    isWhiteBg: false,
    maxWidth: '240px',
    tipEllipsis: false,
    tipEllipsisLine: 1,
    overlayClassName: '',
    overlayStyle: {},
    ellipsis: false,
    width: '',
    containerStyle: {},
    showContainer: false,
    ...props.tooltip,
  }
})

const tooltipEnabled = computed(() => tooltipOptions.value.show)

const tooltipBindings = computed(() => {
  const { show, ...rest } = tooltipOptions.value
  return rest
})

const sizeConfig = computed(() => SIZE_MAP[props.size])

const activeColors = computed(() => {
  if (props.activeColors?.length) {
    return props.activeColors
  }

  if (props.colorTheme) {
    return heatmapColorThemes[props.colorTheme]
  }

  return heatmapColorThemes.green
})

const minimumColor = computed(() => props.minimumColor || '#ebedf0')

const indicatorColors = computed(() => [
  minimumColor.value,
  ...activeColors.value,
])

const indicatorRowClass = computed(() => {
  return [
    bem.e('indicator-row'),
    bem.em('indicator-row', props.indicatorVerticalPosition),
    bem.em('indicator-row', props.indicatorHorizontalAlign),
  ]
})

const cssVars = computed(() => ({
  '--ll-heatmap-rect-size': `${sizeConfig.value.rectSize}px`,
  '--ll-heatmap-radius': `${sizeConfig.value.radius}px`,
  '--ll-heatmap-font-size': `${sizeConfig.value.fontSize}px`,
  '--ll-heatmap-label-width': `${sizeConfig.value.labelWidth}px`,
  '--ll-heatmap-x-gap': addUnit(props.xGap) || '4px',
  '--ll-heatmap-y-gap': addUnit(props.yGap) || '4px',
}))

const displaySource = computed(() => {
  if (props.loading) {
    if (props.loadingData?.length) {
      return props.loadingData
    }

    if (props.data.length) {
      return props.data
    }

    return createRecentLoadingData()
  }

  return props.data
})

const normalizedData = computed<HeatmapCellData[]>(() => {
  if (!displaySource.value.length) {
    return []
  }

  const sorted = [...displaySource.value]
    .map((item) => ({
      timestamp: startOfDay(item.timestamp).getTime(),
      value: item.value ?? null,
    }))
    .sort((a, b) => a.timestamp - b.timestamp)

  const sourceMap = new Map<number, HeatmapDataItem>()

  sorted.forEach((item) => {
    sourceMap.set(item.timestamp, item)
  })

  const firstTimestamp = sorted[0].timestamp
  const lastTimestamp = sorted[sorted.length - 1].timestamp

  const gridStart = startOfWeek(firstTimestamp, props.firstDayOfWeek)
  const rangeStart = props.fillCalendarLeading
    ? gridStart
    : startOfDay(firstTimestamp).getTime()
  const rangeEnd = endOfWeek(lastTimestamp, props.firstDayOfWeek)

  const series: HeatmapCellData[] = []

  for (let timestamp = rangeStart; timestamp <= rangeEnd; timestamp += DAY_MS) {
    const sourceItem = sourceMap.get(timestamp)
    const value = sourceItem?.value ?? null
    const synthetic = !sourceItem

    series.push({
      timestamp,
      value,
      hidden: props.loading ? value === null : false,
      synthetic,
    })
  }

  return series
})

const maxValue = computed(() => {
  return normalizedData.value.reduce((current, item) => {
    const value = typeof item.value === 'number' ? item.value : 0
    return value > current ? value : current
  }, 0)
})

const heatmapMatrix = computed<Array<Array<HeatmapCell | null>>>(() => {
  if (!normalizedData.value.length) {
    return []
  }

  const calendarStart = startOfWeek(
    normalizedData.value[0].timestamp,
    props.firstDayOfWeek,
  )

  const weeks: Array<Array<HeatmapCell | null>> = []

  normalizedData.value.forEach((item) => {
    const rowIndex = getRowIndex(item.timestamp, props.firstDayOfWeek)
    const columnIndex = getColumnIndex(item.timestamp, calendarStart)

    if (!weeks[columnIndex]) {
      weeks[columnIndex] = Array.from({ length: 7 }, () => null)
    }

    weeks[columnIndex][rowIndex] = {
      data: item,
      color: resolveColor(
        item.value,
        maxValue.value,
        activeColors.value,
        minimumColor.value,
      ),
      hidden: item.hidden,
      loading: props.loading && !item.hidden,
    }
  })

  return weeks
})

const monthLabels = computed(() => {
  let lastMonth = ''

  return heatmapMatrix.value.map((week) => {
    const firstVisibleDay = week.find((item) => item !== null)?.data

    if (!firstVisibleDay) {
      return ''
    }

    const month = formatMonth(firstVisibleDay.timestamp)
    if (month !== lastMonth) {
      lastMonth = month
      return month
    }

    return ''
  })
})

const weekLabels = computed(() => {
  const formatter = new Intl.DateTimeFormat(undefined, { weekday: 'short' })
  const reference = getReferenceWeekStart(props.firstDayOfWeek)

  return Array.from({ length: 7 }, (_, index) => {
    return formatter.format(addDays(reference, index))
  })
})

const getTooltipText = (item: HeatmapDataItem) => {
  const dateText = new Date(item.timestamp).toLocaleDateString()
  const value = item.value ?? 0
  const suffix = props.unit ? ` ${value} ${props.unit}` : ` ${value}`
  return `${dateText}${suffix}`
}

const getCellClass = (cell: HeatmapCell) => {
  return [
    bem.e('cell'),
    cell.loading ? bem.is('loading') : '',
    cell.hidden ? bem.is('hidden') : '',
    cell.data.synthetic ? bem.is('synthetic') : '',
  ]
}

const getCellStyle = (cell: HeatmapCell) => {
  return {
    backgroundColor: cell.hidden ? 'transparent' : cell.color,
  }
}

const getCellKey = (
  cell: HeatmapCell | null,
  weekIndex: number,
  dayIndex: number,
) => {
  return cell
    ? `${cell.data.timestamp}-${weekIndex}-${dayIndex}`
    : `empty-${weekIndex}-${dayIndex}`
}

function startOfDay(timestamp: number) {
  const date = new Date(timestamp)
  date.setHours(0, 0, 0, 0)
  return date
}

function addDays(date: Date, days: number) {
  const nextDate = new Date(date)
  nextDate.setDate(nextDate.getDate() + days)
  return nextDate
}

function getJsWeekStart(firstDayOfWeek: number) {
  return (firstDayOfWeek + 1) % 7
}

function getRowIndex(timestamp: number, firstDayOfWeek: number) {
  const weekStart = getJsWeekStart(firstDayOfWeek)
  const day = new Date(timestamp).getDay()
  return (day - weekStart + 7) % 7
}

function startOfWeek(timestamp: number, firstDayOfWeek: number) {
  const date = startOfDay(timestamp)
  return addDays(date, -getRowIndex(date.getTime(), firstDayOfWeek)).getTime()
}

function endOfWeek(timestamp: number, firstDayOfWeek: number) {
  return addDays(new Date(startOfWeek(timestamp, firstDayOfWeek)), 6).getTime()
}

function getColumnIndex(timestamp: number, calendarStart: number) {
  const current = startOfDay(timestamp).getTime()
  return Math.floor((current - calendarStart) / DAY_MS / 7)
}

function resolveColor(
  value: number | null | undefined,
  max: number,
  colors: string[],
  baseColor: string,
) {
  if (value === null || value === undefined || value <= 0 || max <= 0) {
    return baseColor
  }

  const step = Math.min(colors.length, Math.ceil((value / max) * colors.length))
  return colors[Math.max(0, step - 1)] || colors[colors.length - 1] || baseColor
}

function formatMonth(timestamp: number) {
  return new Intl.DateTimeFormat(undefined, { month: 'short' }).format(
    new Date(timestamp),
  )
}

function getReferenceWeekStart(firstDayOfWeek: number) {
  const jsWeekStart = getJsWeekStart(firstDayOfWeek)
  const date = new Date(2024, 0, 7)

  while (date.getDay() !== jsWeekStart) {
    date.setDate(date.getDate() + 1)
  }

  return date
}

function createRecentLoadingData() {
  const today = startOfDay(Date.now())
  const result: HeatmapDataItem[] = []

  for (let index = 364; index >= 0; index -= 1) {
    result.push({
      timestamp: addDays(today, -index).getTime(),
      value: index % 5 === 0 ? 3 : 1,
    })
  }

  return result
}
</script>
